import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSalesmanList } from "../../redux/actions/salesmanAction";
import { fetchSalesmanConsolidatedReport } from "../../redux/actions/salesmanAction";
import DashboardLayout from "../dashboard/DashboardLayout";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../Pages/Components/SearchBar";
import { FaArrowLeft, FaUser, FaQrcode, FaGift, FaCalendar } from "react-icons/fa";
import { FullScreenLoader } from "../Components/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";

const SalesmanReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((state) => state.auth);
  const { salesmen = [], loading: salesmenLoading } = useSelector(
    (state) => state.salesman || {}
  );
  const {
    consolidatedReport,
    reportLoading,
    reportError,
  } = useSelector((state) => state.salesman || {});

  const [searchText, setSearchText] = useState("");
  const [selectedSalesman, setSelectedSalesman] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Check if user is role 1 (Dealer)
  const isRole1 = userInfo?.role_id === "1" || userInfo?.role_id === 1;

  useEffect(() => {
    if (!userInfo?.access_token) {
      navigate("/login");
    } else if (isRole1) {
      // Fetch salesman list for role 1 users
      dispatch(fetchSalesmanList(userInfo.access_token));
    }
  }, [dispatch, userInfo, navigate, isRole1]);

  // Convert single salesman object to array if needed
  const salesmanArray = Array.isArray(salesmen)
    ? salesmen
    : salesmen
    ? [salesmen]
    : [];

  // Filter salesmen based on search
  const filteredSalesmen = salesmanArray.filter((s) => {
    const search = searchText.toLowerCase();
    const salesmanName = s.name?.toLowerCase() || "";
    const salesmanMobile = s.mobile?.toLowerCase() || "";
    const salesmanEmail = s.email?.toLowerCase() || "";
    const salesmanCity = s.city?.toLowerCase() || "";
    const salesmanState = s.state?.toLowerCase() || "";

    return (
      salesmanName.includes(search) ||
      salesmanMobile.includes(search) ||
      salesmanEmail.includes(search) ||
      salesmanCity.includes(search) ||
      salesmanState.includes(search)
    );
  });

  // Handle salesman selection
  const handleSalesmanSelect = async (salesman) => {
    setSelectedSalesman(salesman);
    
    // Prepare data for API call
    const reportData = {
      salesman_id: salesman.user_id,
    };

    // Add date filters if provided
    if (startDate) {
      reportData.start_date = dayjs(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      reportData.end_date = dayjs(endDate).format("YYYY-MM-DD");
    }

    // Call consolidated report API
    await dispatch(
      fetchSalesmanConsolidatedReport(reportData, userInfo.access_token)
    );
  };

  // Handle view all salesmen consolidated report (for dealer role)
  const handleViewAllSalesmenReport = async () => {
    setSelectedSalesman({ name: "All Salesmen", user_id: null });
    
    // Prepare data for API call without salesman_id (will return consolidated for all)
    const reportData = {};

    // Add date filters if provided
    if (startDate) {
      reportData.start_date = dayjs(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      reportData.end_date = dayjs(endDate).format("YYYY-MM-DD");
    }

    // Call consolidated report API without salesman_id
    await dispatch(
      fetchSalesmanConsolidatedReport(reportData, userInfo.access_token)
    );
  };

  // Handle back to salesman list
  const handleBackToList = () => {
    setSelectedSalesman(null);
    setStartDate(null);
    setEndDate(null);
  };

  // If salesman is selected, show consolidated report
  if (selectedSalesman && consolidatedReport) {
    const report = consolidatedReport;
    const summary = report.summary || {};
    const details = report.details || {};

    return (
      <DashboardLayout>
        <div className="bg-priimary max-w-full mx-auto p-4">
          {/* Header */}
          <div className="flex items-center space-x-3 py-4 mb-4">
            <button
              onClick={handleBackToList}
              className="hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-amber-600">
                Salesman Consolidated Report
              </h2>
              <p className="text-sm text-gray-600">
                {selectedSalesman?.user_id 
                  ? `${report.salesman?.name || selectedSalesman.name} (${report.salesman?.mobile || ""})`
                  : "All Salesmen (Consolidated)"}
              </p>
            </div>
          </div>

          {/* Date Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd-MM-yyyy"
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholderText="Select start date"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd-MM-yyyy"
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholderText="Select end date"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => handleSalesmanSelect(selectedSalesman)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Total Customers</p>
                  <p className="text-2xl font-bold text-green-600">
                    {summary.total_customers || 0}
                  </p>
                </div>
                <FaUser className="text-green-300 text-3xl" />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">QR Codes Accumulated</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {summary.total_qr_codes_accumulated || 0}
                  </p>
                </div>
                <FaQrcode className="text-blue-300 text-3xl" />
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">QR Codes Redeemed</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {summary.total_qr_codes_redeemed || 0}
                  </p>
                </div>
                <FaQrcode className="text-purple-300 text-3xl" />
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Gifts Redeemed</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {summary.total_gifts_redeemed || 0}
                  </p>
                </div>
                <FaGift className="text-orange-300 text-3xl" />
              </div>
            </div>
          </div>

          {/* Report Details */}
          {reportLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading report details...</p>
            </div>
          ) : reportError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">
                Error: {reportError.message || "Failed to load report"}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Customers List */}
              {details.customers && details.customers.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">
                    Customers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-300">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left border">Name</th>
                          <th className="px-4 py-2 text-left border">Mobile</th>
                          <th className="px-4 py-2 text-left border">City</th>
                          <th className="px-4 py-2 text-left border">State</th>
                          <th className="px-4 py-2 text-left border">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.customers.map((customer, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border">{customer.name}</td>
                            <td className="px-4 py-2 border">{customer.mobile}</td>
                            <td className="px-4 py-2 border">{customer.city || "-"}</td>
                            <td className="px-4 py-2 border">{customer.state || "-"}</td>
                            <td className="px-4 py-2 border">
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  customer.is_active
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {customer.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Accumulated QR Codes */}
              {details.accumulated_qr_codes &&
                details.accumulated_qr_codes.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">
                      Accumulated QR Codes
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-300">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left border">QR ID</th>
                            <th className="px-4 py-2 text-left border">Scheme</th>
                            <th className="px-4 py-2 text-left border">Amount</th>
                            <th className="px-4 py-2 text-left border">Customer</th>
                            <th className="px-4 py-2 text-left border">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.accumulated_qr_codes.map((qr, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-2 border">{qr.qr_id}</td>
                              <td className="px-4 py-2 border">{qr.qr_scheme}</td>
                              <td className="px-4 py-2 border">
                                {qr.qr_amount || "-"}
                              </td>
                              <td className="px-4 py-2 border">
                                {qr.customer_name || "-"}
                              </td>
                              <td className="px-4 py-2 border">
                                {qr.created_at
                                  ? dayjs(qr.created_at).format("DD-MM-YYYY HH:mm")
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Redeemed QR Codes */}
              {details.redeemed_qr_codes &&
                details.redeemed_qr_codes.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">
                      Redeemed QR Codes
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-gray-300">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left border">QR ID</th>
                            <th className="px-4 py-2 text-left border">Scheme</th>
                            <th className="px-4 py-2 text-left border">Amount</th>
                            <th className="px-4 py-2 text-left border">Customer</th>
                            <th className="px-4 py-2 text-left border">Redeemed At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.redeemed_qr_codes.map((qr, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-2 border">{qr.qr_id}</td>
                              <td className="px-4 py-2 border">{qr.qr_scheme}</td>
                              <td className="px-4 py-2 border">
                                {qr.qr_amount || "-"}
                              </td>
                              <td className="px-4 py-2 border">
                                {qr.customer_name || "-"}
                              </td>
                              <td className="px-4 py-2 border">
                                {qr.redeemed_at
                                  ? dayjs(qr.redeemed_at).format("DD-MM-YYYY HH:mm")
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Gifts Redeemed */}
              {details.gifts_redeemed && details.gifts_redeemed.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">
                    Gifts Redeemed
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-300">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left border">Gift Name</th>
                          <th className="px-4 py-2 text-left border">Scheme</th>
                          <th className="px-4 py-2 text-left border">QR Count</th>
                          <th className="px-4 py-2 text-left border">Customer</th>
                          <th className="px-4 py-2 text-left border">Redeemed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.gifts_redeemed.map((gift, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border">{gift.gift_name}</td>
                            <td className="px-4 py-2 border">
                              {gift.qr_scheme || "-"}
                            </td>
                            <td className="px-4 py-2 border">
                              {gift.qr_count_required || "-"}
                            </td>
                            <td className="px-4 py-2 border">
                              {gift.customer_name || "-"}
                            </td>
                            <td className="px-4 py-2 border">
                              {gift.created_at
                                ? dayjs(gift.created_at).format("DD-MM-YYYY HH:mm")
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {(!details.customers || details.customers.length === 0) &&
                (!details.accumulated_qr_codes ||
                  details.accumulated_qr_codes.length === 0) &&
                (!details.redeemed_qr_codes ||
                  details.redeemed_qr_codes.length === 0) &&
                (!details.gifts_redeemed ||
                  details.gifts_redeemed.length === 0) && (
                  <div className="text-center py-8">
                    <p className="text-gray-600">
                      No data available for the selected date range.
                    </p>
                  </div>
                )}
            </div>
          )}
        </div>
        <FullScreenLoader
          show={reportLoading}
          message="Loading salesman report..."
          variant="primary"
          blurIntensity="md"
        />
      </DashboardLayout>
    );
  }

  // Default view: Show salesman list
  return (
    <DashboardLayout>
      <div className="bg-priimary max-w-full mx-auto p-4">
        {/* Header */}
        <div className="flex items-center space-x-3 py-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold text-amber-600">
            Salesman Reports
          </h2>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <SearchBar
            value={searchText}
            onChange={setSearchText}
            placeholder="Search by salesman name, mobile, email, city, or state..."
          />
        </div>

        {/* View All Salesmen Report Button (for Dealer role) */}
        {isRole1 && (
          <div className="mb-4">
            <button
              onClick={handleViewAllSalesmenReport}
              className="px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium"
            >
              View All Salesmen Consolidated Report
            </button>
          </div>
        )}

        {/* Salesman List */}
        {salesmenLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading salesmen...</p>
          </div>
        ) : filteredSalesmen.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">
              {searchText
                ? "No salesmen found matching your search."
                : "No salesmen available."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left border">Name</th>
                  <th className="px-4 py-3 text-left border">Mobile</th>
                  <th className="px-4 py-3 text-left border">Email</th>
                  <th className="px-4 py-3 text-left border">City</th>
                  <th className="px-4 py-3 text-left border">State</th>
                  <th className="px-4 py-3 text-center border">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredSalesmen.map((salesman) => (
                  <tr
                    key={salesman.user_id}
                    className="hover:bg-amber-50 border-b"
                  >
                    <td className="px-4 py-3 border">{salesman.name || "-"}</td>
                    <td className="px-4 py-3 border">{salesman.mobile || "-"}</td>
                    <td className="px-4 py-3 border">{salesman.email || "-"}</td>
                    <td className="px-4 py-3 border">{salesman.city || "-"}</td>
                    <td className="px-4 py-3 border">{salesman.state || "-"}</td>
                    <td className="px-4 py-3 border text-center">
                      <button
                        onClick={() => handleSalesmanSelect(salesman)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        View Report
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <FullScreenLoader
          show={salesmenLoading}
          message="Loading salesmen..."
          variant="primary"
          blurIntensity="md"
        />
      </div>
    </DashboardLayout>
  );
};

export default SalesmanReport;

